<template>
  <div v-if="showFilesFeature" class="w-full lg:w-1/4">
    <vs-card class="h-full">
      <div class="flex items-center justify-between mb-6">
        <h2 class="text-lg font-semibold text-gray-900">Files</h2>
        <vs-button
          icon-pack="feather"
          icon="icon-plus"
          icon-before
          color="primary"
          class="font-semibold"
          type="flat"
          @click="showUploadDialog = true"
        >
          Add
        </vs-button>
      </div>
    
      <div v-if="loadingFiles" class="space-y-4">
        <div v-for="n in 3" :key="n" class="animate-pulse">
          <div class="flex items-center space-x-3 p-3">
            <div class="w-8 h-8 bg-gray-200 rounded"></div>
            <div class="flex-1">
              <div class="h-4 bg-gray-200 rounded w-3/4 mb-2"></div>
              <div class="h-3 bg-gray-200 rounded w-1/2"></div>
            </div>
          </div>
        </div>
      </div>
      
      <!-- Files list -->
      <div v-else-if="patientFiles.length > 0" class="space-y-2">
        <div 
          v-for="file in patientFiles" 
          :key="file._id" 
          class="flex items-center justify-between py-2 pr-3 hover:bg-gray-50 rounded-lg transition-colors cursor-pointer"
          @click="openFile(file)"
        >
          <div class="flex items-center space-x-3 flex-1 min-w-0">
            <div class="w-8 h-8 rounded flex items-center justify-center flex-shrink-0">
              <vs-icon 
                icon-pack="feather" 
                :icon="getFileIcon(file.type)" 
                size="small"
              ></vs-icon>
            </div>
            <div class="min-w-0 flex-1 overflow-hidden">
              <p
                 class="text-sm font-medium text-gray-900 truncate"
                 :title="file.name"
                 style="max-width: 168px;"
               >
                {{ file.name }}
              </p>
              <p class="text-xs text-gray-500">{{ formatFileDate(file.uploadedAt) }} · {{ file.uploadedBy }}</p>
            </div>
          </div>
          <div class="ml-4 flex-shrink-0" @click.stop>
            <vs-dropdown vs-trigger-click>
              <vs-button
                type="flat"
                size="small"
                class="p-1"
                @click.stop
              >
                <vs-icon icon-pack="feather" icon="icon-more-horizontal" size="small"></vs-icon>
              </vs-button>
            
            <vs-dropdown-menu>
              <vs-dropdown-item @click.stop="openFile(file)">
                View
              </vs-dropdown-item>
              <vs-dropdown-item @click.stop="deleteFile(file)" color="danger">
                Delete
              </vs-dropdown-item>
            </vs-dropdown-menu>
          </vs-dropdown>
          </div>
        </div>
      </div>
      
      <div v-else class="text-center py-12">
        <img
          src="/empty-icon.png"
          alt="No files"
          class="mx-auto mb-4 max-w-[64px] h-auto"
        />
        <p class="text-sm font-semibold text-primary">No files available</p>
        <p class="text-sm text-primary">Click 'Add' to upload a file</p>
      </div>
      
      <div v-if="patientFiles.length > 0" class="flex justify-center mt-1">
        <vs-button
          color="primary"
          type="flat"
          class="font-semibold"
          @click="$emit('viewPatientFiles')"
        >
          View all
        </vs-button>
      </div>
      
    </vs-card>
    
    <PatientFileUploadDialog 
      :open="showUploadDialog"
      :isUploading="isUploading"
      @update:open="showUploadDialog = $event"
      @close="handleDialogClose"
      @refetchPatientFiles="loadPatientFiles"
    />
    
    <!-- File Delete Dialog -->
    <PatientFileDeleteDialog
      :open="showDeleteDialog"
      :fileName="fileToDelete ? fileToDelete.name : ''"
      :fileDate="fileToDelete ? formatFileDate(fileToDelete.uploadedAt) : ''"
      @close="closeDeleteDialog"
      @confirm-delete="confirmFileDeletion"
    />
  </div>
</template>

<script>
import { mapActions } from 'vuex';
import moment from 'moment';
import PatientFileUploadDialog from './PatientFileUploadDialog.vue';
import PatientFileDeleteDialog from './PatientFileDeleteDialog.vue';

export default {
  name: 'PatientFilesSection',
  components: {
    PatientFileUploadDialog,
    PatientFileDeleteDialog,
  },
  props: {
    patientId: {
      type: String,
      required: true,
    },
    showFilesFeature: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      patientFiles: [],
      loadingFiles: false,
      showUploadDialog: false,
      isUploading: false,
      showDeleteDialog: false,
      fileToDelete: null,
    };
  },
  methods: {
    ...mapActions('patient', ['getPatientFiles', 'deletePatientFile']),
    
    moment(date) {
      return moment(date);
    },
    
    async loadPatientFiles() {
      this.loadingFiles = true;
      try {
        const response = await this.getPatientFiles(this.patientId);
        this.patientFiles = (response.data.data.files || []).slice(0, 4);
      } catch (error) {
        this.patientFiles = [];
      } finally {
        this.loadingFiles = false;
      }
    },
    
    formatFileDate(dateString) {
      return moment(dateString).format('DD MMM YYYY');
    },
    
    getFileIcon(fileType) {
      const imageTypes = ['jpeg', 'jpg', 'png', 'gif', 'bmp', 'webp'];
      if (imageTypes.includes(fileType)) {
        return 'icon-image';
      }
      return 'icon-file-text';
    },
    
    openFile(file) {
      window.open(file.url, '_blank');
    },

    handleDialogClose() {
      this.showUploadDialog = false;
      this.isUploading = false;
    },
    
    deleteFile(file) {
      this.fileToDelete = file;
      this.showDeleteDialog = true;
    },
    
    closeDeleteDialog() {
      this.showDeleteDialog = false;
      this.fileToDelete = null;
    },
    
    async confirmFileDeletion() {
      try {
        await this.deletePatientFile(this.fileToDelete._id);
        
        const index = this.patientFiles.findIndex(f => f.id === this.fileToDelete._id);
        if (index > -1) {
          this.patientFiles.splice(index, 1);
        }
        
        this.$vs.notify({
          title: 'Success',
          text: 'File deleted successfully',
          color: 'success',
        });
        
        this.closeDeleteDialog();

        this.loadPatientFiles();
      } catch (error) {
        this.$vs.notify({
          title: 'Error',
          text: error.message || 'Failed to delete file',
          color: 'danger',
        });
      }
    },
  },
  async created() {
    if (this.showFilesFeature && this.patientId) {
      await this.loadPatientFiles();
    }
  },
  watch: {
    patientId: {
      handler(newPatientId) {
        if (this.showFilesFeature && newPatientId) {
          this.loadPatientFiles();
        }
      },
      immediate: true,
    },
    showFilesFeature: {
      handler(newValue) {
        if (newValue && this.patientId) {
          this.loadPatientFiles();
        }
      },
      immediate: true,
    },
  },
};
</script>